import classnames from 'classnames';
import sum from 'lodash/sum';
import { func } from 'prop-types';
import React from 'react';
import { Table } from 'reactstrap';

import { getSpentBudgetForProduct } from '../../campaign-editor/utils';
import CurrencyFormat from '../../generic/CurrencyFormat';
import CurrencyInput from '../../generic/CurrencyInput';
import { campaignShape } from '../../prop-types';
import {
  productAggregatesMapShape,
  WRITABLE_BUDGET_FIELDS,
} from '../change-product-budget/constants';
import {
  calculateBudgetFromNewValues,
  calculateBudgetFromOldValues,
} from '../change-product-budget/utils';
import MoveBudgetToWalletTotals from './MoveBudgetToWalletTotals';

function MoveBudgetToWalletTable({ productAggregatesMap, onChange, campaign }) {
  const productAggregates = Object.values(productAggregatesMap);
  const totalNewBudget = sum(
    productAggregates.map(productAggregate =>
      calculateBudgetFromNewValues(productAggregate, campaign),
    ),
  );
  const totalOldBudget = sum(
    productAggregates.map(productAggregate =>
      calculateBudgetFromOldValues(productAggregate, campaign),
    ),
  );

  return (
    <Table className="table-md table-va-middle mt-5 mb-4 product-budget">
      <thead className="bg-gray-200">
        <tr>
          <th className="border-top-0">Products</th>
          <th className="border-top-0 text-nowrap">Mngmt. Fee</th>
          <th colSpan={3} className="text-center border-top-0">
            Net Client Budget External
          </th>
          <th colSpan={3} className="text-center border-top-0">
            Net Client Budget WKZ
          </th>
          <th colSpan={3} className="text-center border-top-0">
            Free Service
          </th>
          <th colSpan={3} className="text-center border-top-0">
            Co Invest
          </th>
          <th colSpan={2} className="text-center border-top-0">
            Budget
          </th>
          <th className="border-top-0 text-danger">Spent</th>
          <th className="border-top-0 text-center text-success">Left</th>
          <th className="border-top-0 text-info">
            <span className="d-inline-flex align-items-center">Delta</span>
          </th>
        </tr>
        <tr>
          <th>&nbsp;</th>
          <th>&nbsp;</th>
          <th className="text-center">Current</th>
          <th className="text-center">New</th>
          <th className="text-center text-info">Delta</th>
          <th className="text-center">Current</th>
          <th className="text-center">New</th>
          <th className="text-center text-info">Delta</th>
          <th className="text-center">Current</th>
          <th className="text-center">New</th>
          <th className="text-center text-info">Delta</th>
          <th className="text-center">Current</th>
          <th className="text-center">New</th>
          <th className="text-center text-info">Delta</th>
          <th className="text-center">Current</th>
          <th className="text-center">New</th>
          <th>&nbsp;</th>
          <th>&nbsp;</th>
          <th>&nbsp;</th>
        </tr>
      </thead>
      <tbody>
        {Object.entries(productAggregatesMap).map(([id, productAggregate]) => {
          const { adjustedFee, newValues, oldValues, product } =
            productAggregate;

          const spentAmount = getSpentBudgetForProduct(campaign, product);

          const oldBudget = calculateBudgetFromOldValues(
            productAggregate,
            campaign,
          );
          const newBudget = calculateBudgetFromNewValues(
            productAggregate,
            campaign,
          );

          return (
            <tr key={id}>
              <td>{productAggregate.label}</td>
              <td className="text-right">
                {adjustedFee ? (
                  <CurrencyFormat
                    name={`${id}-adjustedFee`}
                    value={adjustedFee * 100}
                    prefix=""
                    suffix=" %"
                    text
                  />
                ) : (
                  '-'
                )}
              </td>
              {WRITABLE_BUDGET_FIELDS.map(field => {
                const delta = newValues[field] - oldValues[field];
                return [
                  <td key={`old-${field}`} className="text-right bg-gray-200">
                    <CurrencyFormat
                      value={oldValues[field]}
                      className="text-nowrap"
                      text
                      name={`productAggregates.${id}.oldValues.${field}`}
                    />
                  </td>,
                  <td key={`new-${field}`} className="text-right bg-gray-200">
                    <CurrencyInput
                      value={newValues[field]}
                      onChange={onChange}
                      name={`productAggregates.${id}.newValues.${field}`}
                      className="wider-input"
                    />
                  </td>,
                  <td
                    key={`delta-${field}`}
                    className={classnames('text-right bg-gray-200', {
                      'cr-border-red': delta > 0,
                      'cr-border-green': delta < 0,
                    })}
                  >
                    <CurrencyFormat
                      name={`${id}.deltaAmount.${field}`}
                      value={delta}
                      className="text-nowrap text-info"
                      text
                    />
                  </td>,
                ];
              })}
              <td className="text-right bg-gray-200">
                <CurrencyFormat
                  name={`${id}-oldBudget`}
                  value={oldBudget}
                  className="text-nowrap"
                  text
                />
              </td>
              <td className="text-right bg-gray-200">
                <CurrencyFormat
                  name={`${id}-newBudget`}
                  value={newBudget}
                  className={classnames('text-nowrap', {
                    'bg-danger text-white': newBudget < spentAmount,
                  })}
                  text
                />
              </td>
              <td className="text-right">
                <CurrencyFormat
                  name={`${id}-spentAmount`}
                  value={spentAmount}
                  className="text-nowrap text-danger"
                  text
                />
              </td>
              <td className="text-right">
                <CurrencyFormat
                  name={`${id}-leftAmount`}
                  value={newBudget - spentAmount}
                  className="text-nowrap text-success"
                  text
                />
              </td>
              <td className="text-right">
                <CurrencyFormat
                  name={`${id}-deltaAmount`}
                  value={newBudget - oldBudget}
                  className={classnames('text-nowrap text-info', {
                    'cr-border-red': newBudget - oldBudget > 0,
                    'cr-border-green': newBudget - oldBudget < 0,
                  })}
                  text
                />
              </td>
            </tr>
          );
        })}
      </tbody>
      <tfoot>
        <MoveBudgetToWalletTotals
          campaign={campaign}
          productAggregatesMap={productAggregatesMap}
          totalNewBudget={totalNewBudget}
          totalOldBudget={totalOldBudget}
          onChange={onChange}
        />
      </tfoot>
    </Table>
  );
}

MoveBudgetToWalletTable.propTypes = {
  onChange: func.isRequired,
  campaign: campaignShape.isRequired,
  productAggregatesMap: productAggregatesMapShape.isRequired,
};

export default MoveBudgetToWalletTable;
